import { useMemo, useState } from "react";
import { useMe } from "../api";
import { useHotels } from "../components/Numbers";

/**
 * Guest Reviews (SPEC §17). Not connected yet: the planned sources are
 * Meridian's brand guest-satisfaction surveys (gss_surveys, already ingested)
 * and RevPar MD's review-site feed. Until those are wired, Admins see SAMPLE
 * reviews (generated, made-up text) on the real hotel list to show the design;
 * client users see a "coming soon" note and never the sample.
 */

const SITES = ["Google", "Tripadvisor", "Booking.com", "Expedia", "Hotels.com"] as const;
type Site = (typeof SITES)[number];
interface Review { id: string; hotelId: string; site: Site; date: string; rating: number; title: string; text: string; responded: boolean }

const GOOD = [
  ["Great stay", "Room was spotless and the front desk team went out of their way to help with a late checkout."],
  ["Will be back", "Easy parking, quiet room, and breakfast had plenty of hot options."],
  ["Friendly staff", "Everyone we met was welcoming. Special thanks to the night auditor who sorted out our reservation."],
  ["Perfect for business", "Fast Wi-Fi, a good desk and a short drive to the office park. Exactly what I needed."],
  ["Clean and comfortable", "Beds were very comfortable and the room was quiet. Shower pressure was excellent."],
  ["Lovely pool", "Kids loved the pool and the staff kept the area tidy all weekend."],
] as const;
const MID = [
  ["Fine for one night", "Room was clean but the AC was noisy. Staff were friendly."],
  ["Good location", "Convenient to the highway. Breakfast was crowded on Saturday morning."],
  ["Decent value", "Nothing fancy, but clean and fairly priced. Elevator was slow."],
] as const;
const BAD = [
  ["Disappointed", "Room wasn't ready at check-in and we waited almost an hour."],
  ["Needs updating", "Carpet was worn and the bathroom fan didn't work. Staff were apologetic."],
  ["Noisy", "Thin walls and a loud group next door until 1 am. Front desk said they'd call but nothing changed."],
] as const;

/** Small deterministic generator so the sample doesn't change on every refresh. */
function rng(seed: string) {
  let h = 2166136261;
  for (const c of seed) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return () => { h += 0x6d2b79f5; let t = h; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const iso = (d: Date) => d.toISOString().slice(0, 10);

function sampleReviews(hotels: Array<{ id: string; name: string }>, today: Date): Review[] {
  const out: Review[] = [];
  for (const h of hotels) {
    const r = rng(h.id);
    const quality = 3.6 + r() * 1.2;                         // each hotel has its own level, 3.6–4.8
    const n = 25 + Math.floor(r() * 40);                     // reviews over the last ~400 days
    for (let i = 0; i < n; i++) {
      const daysAgo = Math.floor(Math.pow(r(), 1.6) * 400);  // more recent reviews than old ones
      const rating = Math.max(1, Math.min(5, Math.round(quality + (r() - 0.5) * 2.4)));
      const pool = rating >= 4 ? GOOD : rating === 3 ? MID : BAD;
      const [title, text] = pool[Math.floor(r() * pool.length)]!;
      const d = new Date(today); d.setDate(d.getDate() - daysAgo);
      out.push({ id: `${h.id}-${i}`, hotelId: h.id, site: SITES[Math.floor(r() * SITES.length)]!, date: iso(d), rating, title, text, responded: daysAgo > 6 ? r() < 0.85 : r() < 0.3 });
    }
  }
  return out.sort((a, b) => b.date.localeCompare(a.date));
}

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const stars = (n: number) => "★".repeat(n) + "☆".repeat(5 - n);
const fmtDate = (s: string) => new Date(s + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

export function GuestReviews() {
  const me = useMe().data;
  return (
    <>
      <div className="eyebrow">Owners Module · Guest Reviews</div>
      <h1>Guest Reviews</h1>
      {me?.level === "admin" ? <SampleReviews /> : <div className="card muted">Guest reviews for your hotels are coming soon.</div>}
    </>
  );
}

function SampleReviews() {
  const hotels = useHotels().data?.hotels ?? [];
  const today = useMemo(() => new Date(), []);
  const all = useMemo(() => sampleReviews(hotels, today), [hotels, today]);
  const [sort, setSort] = useState<"low" | "high" | "name">("low");
  const [popup, setPopup] = useState<string | null>(null);
  const cut = (days: number) => { const d = new Date(today); d.setDate(d.getDate() - days); return iso(d); };
  const d30 = cut(30), d60 = cut(60);

  const rows = hotels.map((h) => {
    const mine = all.filter((r) => r.hotelId === h.id);
    const recent = mine.filter((r) => r.date >= d30), prior = mine.filter((r) => r.date >= d60 && r.date < d30);
    const a = avg(recent.map((r) => r.rating)), p = avg(prior.map((r) => r.rating));
    // Brand guest-satisfaction score (planned from Meridian's GSS ingest) — sample, on a 10-point scale.
    const gss = Math.min(10, Math.max(6, 7.4 + ((a ?? 4) - 4) * 1.6 + (rng(h.id + "gss")() - 0.5) * 0.6));
    return { h, recent, rating: a, change: a !== null && p !== null ? a - p : null, unanswered: recent.filter((r) => !r.responded).length, gss };
  }).sort((x, y) => sort === "name" ? x.h.name.localeCompare(y.h.name)
    : ((sort === "low" ? 1 : -1) * ((x.rating ?? 9) - (y.rating ?? 9))));

  const recentAll = all.filter((r) => r.date >= d30);
  const portfolio = avg(recentAll.map((r) => r.rating));

  return (
    <>
      <div className="card" style={{ borderTop: "4px solid #b42318" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
          <h2 style={{ margin: 0 }}>Recent reviews · last 30 days</h2>
          <span className="pill bad-pill">SAMPLE DATA · Admins only</span>
        </div>
        <p className="muted" style={{ fontSize: 13 }}>
          Made-up reviews on your real hotel list, to show the design. Planned sources: brand guest-satisfaction surveys from Meridian and review sites
          (Google, Tripadvisor, Booking.com, Expedia, Hotels.com) from RevPar MD. Client users see "coming soon" until those are connected.
        </p>
        <p>
          Portfolio: <strong>{portfolio?.toFixed(2) ?? "—"}</strong> / 5 from {recentAll.length} reviews ·{" "}
          <strong>{recentAll.filter((r) => !r.responded).length}</strong> without a response yet.
        </p>
        <div className="row" style={{ marginBottom: 10 }}>
          <button className={`btn ${sort === "low" ? "" : "secondary"}`} onClick={() => setSort("low")}>Lowest rated</button>
          <button className={`btn ${sort === "high" ? "" : "secondary"}`} onClick={() => setSort("high")}>Highest rated</button>
          <button className={`btn ${sort === "name" ? "" : "secondary"}`} onClick={() => setSort("name")}>A–Z</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead><tr><th>Hotel</th><th className="num">Rating</th><th className="num">vs prior 30 days</th><th className="num">Reviews</th><th className="num">No response yet</th><th className="num">Brand guest survey</th></tr></thead>
            <tbody>{rows.map((x) => (
              <tr key={x.h.id} onClick={() => setPopup(x.h.id)} style={{ cursor: "pointer" }}>
                <td>{x.h.name}</td>
                <td className="num">{x.rating?.toFixed(2) ?? "—"}</td>
                <td className={`num ${x.change === null ? "" : x.change >= 0 ? "good" : "bad"}`}>{x.change === null ? "—" : `${x.change >= 0 ? "+" : "−"}${Math.abs(x.change).toFixed(2)}`}</td>
                <td className="num">{x.recent.length}</td>
                <td className={`num ${x.unanswered ? "bad" : ""}`}>{x.unanswered}</td>
                <td className="num">{x.gss.toFixed(1)} / 10</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>Click a hotel to read its reviews.</p>
      </div>
      <PastReviews hotels={hotels} all={all} today={today} />
      {popup && <HotelPopup name={hotels.find((h) => h.id === popup)?.name ?? ""} reviews={all.filter((r) => r.hotelId === popup && r.date >= d30)} onClose={() => setPopup(null)} />}
    </>
  );
}

function ReviewItem({ r }: { r: Review }) {
  return (
    <div style={{ borderBottom: "1px solid var(--line)", padding: "8px 0" }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span><span style={{ color: "#c9a45c" }}>{stars(r.rating)}</span> <strong>{r.title}</strong></span>
        <span className="muted" style={{ fontSize: 12 }}>{r.site} · {fmtDate(r.date)}</span>
      </div>
      <div style={{ fontSize: 14 }}>{r.text}</div>
      {!r.responded && <span className="pill warn-pill" style={{ marginTop: 4, display: "inline-block" }}>No response yet</span>}
    </div>
  );
}

function HotelPopup({ name, reviews, onClose }: { name: string; reviews: Review[]; onClose: () => void }) {
  const [site, setSite] = useState<Site | "all">("all");
  const shown = reviews.filter((r) => site === "all" || r.site === site);
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" style={{ width: 680 }} onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 style={{ margin: 0 }}>{name}</h2>
          <button className="btn secondary" onClick={onClose}>Close</button>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>Last 30 days · SAMPLE DATA · for reporting only</p>
        <div className="row" style={{ marginBottom: 8, flexWrap: "wrap" }}>
          <button className={`btn ${site === "all" ? "" : "secondary"}`} onClick={() => setSite("all")}>All · {reviews.length}</button>
          {SITES.map((s) => <button key={s} className={`btn ${site === s ? "" : "secondary"}`} onClick={() => setSite(s)}>{s} · {reviews.filter((r) => r.site === s).length}</button>)}
        </div>
        {shown.length ? shown.map((r) => <ReviewItem key={r.id} r={r} />) : <p className="muted">No reviews from this site in the last 30 days.</p>}
      </div>
    </div>
  );
}

function PastReviews({ hotels, all, today }: { hotels: Array<{ id: string; name: string }>; all: Review[]; today: Date }) {
  const [hotelId, setHotelId] = useState("");
  const [period, setPeriod] = useState<"90" | "180" | "365" | "ytd" | "all">("90");
  const [sort, setSort] = useState<"new" | "low" | "high">("new");
  const id = hotelId || hotels[0]?.id || "";
  const from = period === "all" ? "0000" : period === "ytd" ? `${today.getFullYear()}-01-01` : (() => { const d = new Date(today); d.setDate(d.getDate() - Number(period)); return iso(d); })();
  const list = all.filter((r) => r.hotelId === id && r.date >= from)
    .sort((a, b) => sort === "new" ? b.date.localeCompare(a.date) : sort === "low" ? a.rating - b.rating || b.date.localeCompare(a.date) : b.rating - a.rating || b.date.localeCompare(a.date));
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Past reviews</h2>
      <div className="row" style={{ marginBottom: 10 }}>
        <select value={id} onChange={(e) => setHotelId(e.target.value)}>{hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}</select>
        <select value={period} onChange={(e) => setPeriod(e.target.value as typeof period)}>
          <option value="90">Last 90 days</option><option value="180">Last 6 months</option><option value="365">Last 12 months</option><option value="ytd">Year to date</option><option value="all">All</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="new">Newest first</option><option value="low">Lowest rated first</option><option value="high">Highest rated first</option>
        </select>
        <span className="muted" style={{ fontSize: 12 }}>{list.length} reviews · average {avg(list.map((r) => r.rating))?.toFixed(2) ?? "—"} · SAMPLE</span>
      </div>
      {list.slice(0, 50).map((r) => <ReviewItem key={r.id} r={r} />)}
      {list.length > 50 && <p className="muted" style={{ fontSize: 12 }}>Showing the first 50.</p>}
    </div>
  );
}
